import { ChangeEvent, ClipboardEvent, forwardRef, KeyboardEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import cn from 'classnames';

import Input, { UtilType } from '@/components/common/Input';
import FormStateMessage, { FormStateType } from '@/components/Form/FormStateMessage';
import { FormProps } from '@/components/Form/types/FormProps';
import { getFormErrorMessage } from '@/utils/form';
import { isEmptyObject } from '@/utils/object';

export enum ErrorClassName {
  OUTER_MESSAGE = 'error_msg',
  INNER_MESSAGE = 'error'
}

export type TextFormProps = {
  width?: number;
  placeholder?: string;
  label: string;
  maxLength?: number;
  readOnly?: boolean;
  disabled?: boolean;
  utilType?: UtilType;
  onClickUtil?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  className?: string;
  errorClassName?: ErrorClassName;
  successMessage?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** 공백 입력 불가 여부 체크 */
  spaceDisallowed?: boolean;
  isPhoneNumber?: boolean;
} & FormProps;

const OPT_CLASS_NAME = {
  [UtilType.Search]: 'opt_search',
  [UtilType.Count]: 'opt_count',
  [UtilType.Delete]: ''
};

const TextForm = forwardRef<HTMLInputElement, TextFormProps>(
  (
    {
      registerName,
      registerOption,
      width,
      placeholder,
      label,
      maxLength,
      readOnly = false,
      disabled = false,
      utilType,
      onClickUtil,
      onKeyDown,
      onPaste,
      className,
      errorClassName = ErrorClassName.OUTER_MESSAGE,
      successMessage = '',
      onChange = () => {},
      spaceDisallowed = false,
      isPhoneNumber = false
    },
    ref
  ) => {
    const { register, formState, clearErrors, control } = useFormContext();
    const { errors } = formState;
    const errorMessage = getFormErrorMessage(errors, registerName);
    const currentText = useWatch({ name: registerName, control }) || '';
    const formStateType = successMessage ? FormStateType.SUCCESS : FormStateType.ERROR;

    const { ref: hookFormRef, ...formRegisterRest } = register(registerName, registerOption);

    const onChangeForm = (e: ChangeEvent<HTMLInputElement>) => {
      !isEmptyObject(errors) && clearErrors(registerName);

      const { target } = e;

      if (spaceDisallowed) {
        target.value = target.value.replace(/\s/g, '');
      }

      if (isPhoneNumber) {
        target.value = target.value.replace(/[^0-9-]/g, '');
      }

      formRegisterRest.onChange(e);
      onChange(e);
    };

    return (
      <div className={cn('item_form', { [`w_${width}`]: !!width })}>
        <div className={cn('opt_comm', utilType && OPT_CLASS_NAME[utilType])}>
          <div
            className={cn('box_tf', {
              [errorClassName]: !!errorMessage,
              error_msg: errorMessage,
              success_msg: !!successMessage,
              read_only: readOnly,
              disabled
            })}
          >
            <Input
              className={className}
              labelTitle={label}
              id={`text-${registerName}`}
              placeholder={placeholder}
              maxLength={maxLength}
              readOnly={readOnly}
              disabled={disabled}
              utilType={utilType}
              onUtilClick={onClickUtil}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              value={currentText}
              {...formRegisterRest}
              onChange={onChangeForm}
              ref={(e) => {
                hookFormRef(e);
                if (ref) {
                  typeof ref === 'function' ? ref(e) : (ref.current = e);
                }
              }}
            />
          </div>
          {(errorClassName === ErrorClassName.OUTER_MESSAGE || !!successMessage) && (
            <FormStateMessage message={successMessage || errorMessage} type={formStateType} />
          )}
        </div>
      </div>
    );
  }
);

TextForm.displayName = 'TextForm';

export default TextForm;
